/**
 * LocationPhotoStrip — 3-photo Unsplash strip below the hero on any
 * location page.
 *
 * Two ways to use:
 *   1) Pass `photos={arrayOfPhotos}` (preferred — reuses parent's fetch)
 *   2) Pass `query="..."` (falls back to its own fetch, adds 1 Unsplash call)
 *
 * Attribution per Unsplash guidelines: each thumb links to the photo
 * on Unsplash, and photographer credit is stamped under each image.
 */
import { useContextualPhotos } from "@/hooks/useContextualPhoto";

export default function LocationPhotoStrip({ query, photos: photosProp, count = 3 }) {
  // Only fetch if we weren't handed a `photos` array by the parent —
  // avoids duplicate Unsplash calls when hero + strip share a query.
  const fetched = useContextualPhotos(photosProp ? null : query, { count });
  const photos = (photosProp && photosProp.length ? photosProp : fetched).slice(0, count);
  if (!photos.length) return null;

  return (
    <section
      className="bg-white border rounded-2xl p-4 sm:p-5"
      data-testid="wp-photo-strip"
    >
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">
        Views from Unsplash
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {photos.map((p, i) => (
          <a
            key={p.id || i}
            href={p.unsplash_url}
            target="_blank"
            rel="noreferrer"
            className="group relative block aspect-[16/10] overflow-hidden rounded-xl bg-slate-100"
            style={{ background: p.color || "#e2e8f0" }}
            data-testid={`wp-photo-${i}`}
          >
            <img
              src={p.url}
              alt={p.alt || query || ""}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
              <div className="text-[10px] text-white/85 truncate">
                Photo by{" "}
                <a
                  href={p.credit_url}
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-white"
                  onClick={(e) => e.stopPropagation()}
                >
                  {p.credit_name}
                </a>
                {" "}on Unsplash
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}
